import ShareContentUsageLinkModal from "@/content-usage/buttons/share/ShareContentUsageLinkModal"
import { ShareContentUsageUtils } from "@/content-usage/buttons/share/ShareContentUsageUtils"
import { CopyContentUsageLinkButtonFragment$key } from "@/content-usage/buttons/share/__generated__/CopyContentUsageLinkButtonFragment.graphql"
import { LocationState } from "@/core/route/util/routeUtils"
import { DiscoButtonProps } from "@disco-ui"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import { useCopyToClipboard } from "@utils/dom/domUtils"
import useDisclosure from "@utils/hook/useDisclosure"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"
import { useHistory, useLocation } from "react-router"

interface CopyContentUsageLinkButtonProps extends TestIDProps, DiscoButtonProps {
  contentUsageKey: CopyContentUsageLinkButtonFragment$key
  children: OverridableDiscoButtonChildren
}

const CopyContentUsageLinkButton = observer(function CopyContentUsageLinkButton({
  contentUsageKey,
  testid,
  children,
  ...props
}: CopyContentUsageLinkButtonProps) {
  const history = useHistory<LocationState>()
  const location = useLocation<LocationState>()
  const copyToClipboard = useCopyToClipboard()

  const contentUsage = useFragment<CopyContentUsageLinkButtonFragment$key>(
    graphql`
      fragment CopyContentUsageLinkButtonFragment on ContentUsage {
        id
        entity
        module {
          id
        }
        content {
          label
        }
        ...ShareContentUsageUtils_useShareUrlFragment
      }
    `,
    contentUsageKey
  )

  const shareUrl = ShareContentUsageUtils.useShareUrl(contentUsage)

  // exists when the modal should be opened on item creation
  const shouldOpenLinkModal =
    location.state?.shareContentUsageId === contentUsage.id &&
    // organization usages created by surveys should always open the share modal on creation
    contentUsage.entity === "organization"

  const { isOpen: isLinkModalOpen, onClose: onLinkModalClose } =
    useDisclosure(shouldOpenLinkModal)

  return (
    <>
      <OverridableDiscoButton onClick={handleClick} testid={testid} {...props}>
        {children}
      </OverridableDiscoButton>
      {isLinkModalOpen && (
        <ShareContentUsageLinkModal
          isOpen={isLinkModalOpen}
          onClose={handleClose}
          contentUsageId={contentUsage.id}
          title={`Share ${contentUsage.content.label}`}
        />
      )}
    </>
  )

  function handleClick() {
    copyToClipboard(shareUrl)
  }

  function handleClose() {
    // remove the id from state so the modal won't be reopened on refresh
    if (shouldOpenLinkModal)
      history.replace({
        ...location,
        state: { ...location.state, shareContentUsageId: undefined },
      })
    onLinkModalClose()
  }
})

export default CopyContentUsageLinkButton
